import React, { useRef } from "react";
import { createPortal } from "react-dom";
import { MODAL_BACKDROP_COLOUR, MODAL_Z_INDEX } from "../constants/modal";

interface ModalOverlayProps {
  children: React.ReactNode;
  panelClassName?: string;
  onBackdropClick?: () => void;
  labelledBy?: string;
}

// Colours are inline as well as Tailwind: tailwind.config.js replaces the whole
// palette, so a missing colour utility silently renders a see-through modal.
// Portalled to <body> so no ancestor stacking context or Preline overlay can sit on top.
// The overlay scrolls and an inner min-h-full wrapper centres: centring inside the
// scroller itself would push the top of a tall panel out of reach. Give a fixed-height
// panel viewport units (h-[83vh]): a percentage height resolves to auto under min-h-full.
const ModalOverlay: React.FC<ModalOverlayProps> = ({
  children,
  panelClassName = "w-full max-w-md p-6",
  onBackdropClick,
  labelledBy,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);

  return createPortal(
    <div
      data-modal-overlay
      className="fixed inset-0 overflow-y-auto"
      style={{ zIndex: MODAL_Z_INDEX, backgroundColor: MODAL_BACKDROP_COLOUR }}
      onClick={(e) => {
        if (!panelRef.current?.contains(e.target as Node)) onBackdropClick?.();
      }}
    >
      <div className="flex min-h-full items-center justify-center py-4">
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={labelledBy}
          className={`bg-white rounded-lg shadow-xl mx-4 ${panelClassName}`}
          style={{ backgroundColor: "white" }}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ModalOverlay;
